import * as R from 'ramda';
// icons
import * as I from '../svgs';
// constants
import * as GC from '../constants';
// helpers
import { getTheme } from './theme';
import { isZero, ifElse, isNotNil, isNotZero } from './helpers';
//////////////////////////////////////////////////

const spreadUiStyles = (styles) => ifElse(
  R.is(Object, styles),
  styles,
  {},
);

const mergeAndSpreadUiStyles = (defaultStyles, incoming = {}) => spreadUiStyles(
  R.mergeRight(defaultStyles, incoming),
);

const scrollIntoElementView = ({ elementId }) => {
  const element = document.getElementById(elementId);

  if (isNotNil(element)) element.scrollIntoView({ inline: 'center' });
};

const setElementBorderRight = (index) => ifElse(
  isZero(R.modulo(index, 2)),
  '1px solid',
  '',
);

const setElementBorderBottom = (index, length) => {
  const mod = R.modulo(index, 2);
  const evenElem = isZero(mod);
  const oddElem = isNotZero(mod);
  const lastElem = R.subtract(length, 1);
  const penultimateElement = R.subtract(length, 2);

  const evenElementAndPenultimate = R.and(
    evenElem,
    R.or(R.equals(penultimateElement, index), R.equals(lastElem, index)),
  );

  const addElementAndLast = R.and(oddElem, R.equals(lastElem, index));

  return ifElse(R.or(evenElementAndPenultimate, addElementAndLast), '', '1px solid');
};

const convertDataTypeToUiField = (field) => {
  if (R.is(Function, field)) return field();

  if (R.isNil(field)) return '';

  if (R.is(Boolean, field)) {
    return ifElse(
      R.equals(field, true),
      I.uiTrue,
      I.uiFalse,
    )();
  }

  if (R.is(Object, field)) return R.pathOr('', ['displayedValue'], field);

  return field;
};

const convertDataTypeToUiField2 = (field) => {
  if (R.is(Function, field)) return field();

  if (R.isNil(field)) return I.uiFalse();

  if (R.is(Boolean, field)) {
    return ifElse(
      R.equals(field, true),
      I.uiTrue,
      I.uiFalse,
    )();
  }

  if (R.is(Object, field)) return R.pathOr('', ['displayedValue'], field);

  return field;
};

// helpers for getting main logo link
const renderLogoLink = (currentTheme) => (
  `https://s3.amazonaws.com/amous-front-dev-v2/logo/${currentTheme}-logo.svg`
);

const getNewLogoLink = () => 'https://amous-front-dev-v2.s3.amazonaws.com/newAmousLogo.svg';

const getLogoLink = () => 'https://s3.amazonaws.com/amous-front-dev-v2/logo/light-logo.svg';

// helper for compose image link
const composeImgLink = (themeName, name) => {
  if (R.isNil(name)) return;

  if (R.not(themeName)) { return `https://s3.amazonaws.com/amous-front-dev-v2/icons/${name}.svg`; }

  return `https://s3.amazonaws.com/amous-front-dev-v2/icons/${themeName}/${themeName}-${name}.svg`;
};

const getDocumentIconByDocumentFileName = (documentFilename, width, height) => {
  const fileExtension = R.compose(
    R.last,
    R.split('.'),
  )(R.or(documentFilename, ''));

  const iconMap = {
    [GC.EXTENSION_PDF]: I.pdf,
    [GC.EXTENSION_DOC]: I.word,
    [GC.EXTENSION_XLS]: I.excel,
    [GC.EXTENSION_DOCX]: I.word,
    [GC.EXTENSION_XLSX]: I.excel,
  };

  const defaultIcon = I.documents;

  return R.pathOr(defaultIcon, [fileExtension], iconMap)(null, width, height);
};

const renderBorderColor = ({ hasError }) => {
  if (hasError) {
    return getTheme('forms.inputs.borderColorErr');
  }

  return getTheme('colors.dark.grey');
};

// NOTE: not used for now. We can use this approach to set modal or expanded container zIndex
// higher than all opened expanded containers and modals. Need to add expanded-container className
// to wrapper
const getHighestZIndexFromModalOrExpandedContainer = () => {
  let highestZIndex = -Infinity;

  const elements = document.querySelectorAll('body *');

  elements.forEach((element) => {
    const styles = window.getComputedStyle(element);
    const className = element.className;

    const condition = R.or(
      R.includes('expanded-container', className),
      R.includes('ModalWrapper', className),
    );

    if (condition) {
      const zIndexString = styles.getPropertyValue('z-index');
      const zIndex = parseInt(zIndexString, 10);

      if (!isNaN(zIndex) && zIndex > highestZIndex) {
        highestZIndex = zIndex;
      }
    }
  });

  return highestZIndex;
};

const getStatusTextColor = (status, defaultColor) =>
  getTheme(`${GC.FIELD_STATUS}.${status}.${GC.FIELD_TEXT}`, defaultColor);

export {
  getLogoLink,
  spreadUiStyles,
  renderLogoLink,
  getNewLogoLink,
  composeImgLink,
  renderBorderColor,
  getStatusTextColor,
  scrollIntoElementView,
  setElementBorderRight,
  mergeAndSpreadUiStyles,
  setElementBorderBottom,
  convertDataTypeToUiField,
  convertDataTypeToUiField2,
  getDocumentIconByDocumentFileName,
  getHighestZIndexFromModalOrExpandedContainer,
};
